import type { JSX } from "react";
import { useMemo, useReducer, useState } from "react";
import {
  type GraphEdgeEvent,
  type GraphNodeEvent,
  type NodeDefinition,
  type NodeId,
  type NodeState,
  type PactEvent,
  type UnifiedEvent,
  NODE_DEFINITIONS,
  NODES,
  isGraphEdgeEvent,
  isGraphNodeEvent,
  isPactEvent,
} from "../lib/agent-graph-types";
import type { CarrierContext } from "../lib/useCarrierContext";
import { useUnifiedStream } from "../lib/useUnifiedStream";

// UC-12 Agent Graph Panel — DAG visualization of agent activity
// for one Triton turn. Renders beside the chat surface so consumers
// see the agent-to-agent economy execute in real time.
//
// Lane layout: fixed 6 lanes top-to-bottom (Triton → Twins →
// Aristotle/Loyalty → PACT broker → Negotiator → Settlement/Crypto/
// Midnight). Nodes within a lane are spread horizontally.
//
// Carrier-neutral: the Settlement node's label switches between
// "paypay_402" and "coincheck_spot" via the CarrierContext prop;
// KDDI deployments highlight the Crypto Wallet + Loyalty nodes
// since those are the differentiated rails.

const LANE_HEIGHT = 88;
const NODE_WIDTH = 168;
const NODE_HEIGHT = 56;
const PANEL_WIDTH = 480;

// Color per state — keeps the visual legible at a glance.
const STATE_FILL: Record<NodeState, string> = {
  idle:   "#1f2937",      // dim slate
  active: "#2563eb",      // blue (working)
  done:   "#16a34a",      // green (settled)
  failed: "#dc2626",      // red
};

const STATE_STROKE: Record<NodeState, string> = {
  idle:   "#374151",
  active: "#60a5fa",
  done:   "#4ade80",
  failed: "#f87171",
};


interface PanelState {
  nodeStates: Record<NodeId, NodeState>;
  nodeLabels: Record<NodeId, string>;
  edgeFires: Array<GraphEdgeEvent & { id: string }>;  // animated, bounded
  pactEvents: PactEvent[];
}


type PanelAction =
  | { kind: "node"; event: GraphNodeEvent }
  | { kind: "edge"; event: GraphEdgeEvent }
  | { kind: "pact"; event: PactEvent }
  | { kind: "reset" };


const INITIAL_STATE: PanelState = {
  nodeStates: NODES.reduce(
    (acc, id) => ({ ...acc, [id]: "idle" as NodeState }),
    {} as Record<NodeId, NodeState>,
  ),
  nodeLabels: NODES.reduce(
    (acc, id) => ({ ...acc, [id]: NODE_DEFINITIONS[id].label }),
    {} as Record<NodeId, string>,
  ),
  edgeFires: [],
  pactEvents: [],
};


function reducer(state: PanelState, action: PanelAction): PanelState {
  switch (action.kind) {
    case "reset":
      return INITIAL_STATE;
    case "node":
      return {
        ...state,
        nodeStates: { ...state.nodeStates, [action.event.node_id]: action.event.state },
        nodeLabels: {
          ...state.nodeLabels,
          [action.event.node_id]: action.event.label || state.nodeLabels[action.event.node_id],
        },
      };
    case "edge": {
      // Bound the animated-edge list at 24 most recent.
      const id = `${action.event.from_node_id}->${action.event.to_node_id}-${action.event.at}`;
      const next = [...state.edgeFires, { ...action.event, id }].slice(-24);
      return { ...state, edgeFires: next };
    }
    case "pact":
      return { ...state, pactEvents: [...state.pactEvents, action.event].slice(-50) };
  }
}


interface AgentGraphPanelProps {
  turnId: string | null;
  dealId?: string | null;
  carrier: CarrierContext;
}


export function AgentGraphPanel({
  turnId, dealId, carrier,
}: AgentGraphPanelProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [selectedNode, setSelectedNode] = useState<NodeId | null>(null);

  useUnifiedStream({
    turnId,
    dealId,
    onEvent: (event: UnifiedEvent) => {
      if (isGraphNodeEvent(event)) {
        dispatch({ kind: "node", event });
      } else if (isGraphEdgeEvent(event)) {
        dispatch({ kind: "edge", event });
      } else if (isPactEvent(event)) {
        dispatch({ kind: "pact", event });
      }
    },
  });

  // Carrier-aware node labels: the Settlement node renders the
  // current adapter id + brand; KDDI deployments add a "Coincheck"
  // ribbon on the Crypto Wallet node.
  const carrierAwareLabels = useMemo(() => {
    const next = { ...state.nodeLabels };
    next.settlement = `${carrier.settlementBrand} · ${carrier.adapterId}`;
    if (carrier.adapterId === "coincheck_spot") {
      next.crypto_wallet = "Crypto Wallet · Coincheck";
    }
    next.loyalty = carrier.loyaltyProgramName;
    return next;
  }, [state.nodeLabels, carrier]);

  const nodePositions = useMemo(() => layoutByLane(NODES, NODE_DEFINITIONS), []);

  return (
    <aside
      className="agent-graph-panel"
      style={{
        width: PANEL_WIDTH,
        background: "#0f172a",
        color: "#e2e8f0",
        padding: "16px",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
      aria-label="Agent execution graph"
    >
      <header style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          Agent graph · live
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
          {turnId ? `turn ${turnId.slice(0, 12)}…` : "idle — start a chat turn"}
          {" · "}
          {carrier.settlementBrand}
          {carrier.adapterId === "coincheck_spot" ? " · Coincheck" : ""}
        </div>
      </header>

      <svg
        width={PANEL_WIDTH - 32}
        height={LANE_HEIGHT * 6 + 24}
        style={{ display: "block" }}
        role="img"
        aria-label="Agent execution DAG"
      >
        {/* Edges first so nodes render on top */}
        {state.edgeFires.map((edge) => (
          <EdgeLine
            key={edge.id}
            from={nodePositions[edge.from_node_id]}
            to={nodePositions[edge.to_node_id]}
            kind={edge.kind}
          />
        ))}
        {/* Nodes */}
        {NODES.map((id) => (
          <NodeRect
            key={id}
            pos={nodePositions[id]}
            state={state.nodeStates[id]}
            label={carrierAwareLabels[id]}
            description={NODE_DEFINITIONS[id].description}
            selected={selectedNode === id}
            onSelect={() => setSelectedNode(selectedNode === id ? null : id)}
          />
        ))}
      </svg>

      {/* PACT event log */}
      <section style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
          PACT events {state.pactEvents.length ? `(${state.pactEvents.length})` : ""}
        </div>
        <div
          style={{
            maxHeight: 140, overflowY: "auto",
            background: "#1e293b", borderRadius: 6, padding: 8,
            fontSize: 11, fontFamily: '"SF Mono", Menlo, monospace',
            color: "#cbd5e1",
          }}
        >
          {state.pactEvents.length === 0 ? (
            <span style={{ color: "#64748b" }}>(no events yet)</span>
          ) : (
            state.pactEvents.map((e, i) => (
              <div key={i} style={{ marginBottom: 2 }}>
                <span style={{ color: "#7dd3fc" }}>{e.type}</span>{" "}
                <span>{(e.provider_name as string) ?? (e.deal_id as string) ?? ""}</span>
                {e.price_jpy ? (
                  <span style={{ color: "#fbbf24" }}> ¥{(e.price_jpy as number).toLocaleString()}</span>
                ) : null}
              </div>
            ))
          )}
        </div>
      </section>

      {/* Inspect drawer */}
      {selectedNode && (
        <section
          style={{
            marginTop: 12, padding: 10, background: "#1e293b",
            borderRadius: 6, fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {carrierAwareLabels[selectedNode]}
          </div>
          <div style={{ color: "#94a3b8" }}>
            {NODE_DEFINITIONS[selectedNode].description}
          </div>
          <div style={{ marginTop: 6, color: "#cbd5e1" }}>
            State: {state.nodeStates[selectedNode]}
          </div>
        </section>
      )}
    </aside>
  );
}


// ── Layout ─────────────────────────────────────────────────────────


interface NodePos { x: number; y: number; cx: number; cy: number; }


function layoutByLane(
  ids: readonly NodeId[],
  defs: Record<NodeId, NodeDefinition>,
): Record<NodeId, NodePos> {
  // Group by lane; within a lane spread evenly horizontally.
  const byLane: NodeId[][] = [[], [], [], [], [], []];
  for (const id of ids) {
    const lane = defs[id].lane;
    const bucket = byLane[lane];
    if (bucket) bucket.push(id);
  }
  const out: Partial<Record<NodeId, NodePos>> = {};
  const usableWidth = PANEL_WIDTH - 64;
  byLane.forEach((laneIds, laneIdx) => {
    laneIds.forEach((id, slotIdx) => {
      const slotCount = laneIds.length;
      const slotWidth = usableWidth / slotCount;
      const cx = 16 + slotWidth * (slotIdx + 0.5);
      const cy = 24 + LANE_HEIGHT * laneIdx + NODE_HEIGHT / 2;
      out[id] = {
        x: cx - NODE_WIDTH / 2,
        y: cy - NODE_HEIGHT / 2,
        cx, cy,
      };
    });
  });
  return out as Record<NodeId, NodePos>;
}


// ── Node + Edge SVG ────────────────────────────────────────────────


interface NodeRectProps {
  pos: NodePos;
  state: NodeState;
  label: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}


function NodeRect({
  pos, state, label, description, selected, onSelect,
}: NodeRectProps): JSX.Element {
  return (
    <g
      onClick={onSelect}
      style={{ cursor: "pointer" }}
      role="button"
      aria-label={`${label}: ${description}`}
      aria-pressed={selected}
    >
      <rect
        x={pos.x}
        y={pos.y}
        width={Math.min(NODE_WIDTH, pos.x + NODE_WIDTH > PANEL_WIDTH - 16 ? PANEL_WIDTH - 16 - pos.x : NODE_WIDTH)}
        height={NODE_HEIGHT}
        rx={8}
        fill={STATE_FILL[state]}
        stroke={selected ? "#fbbf24" : STATE_STROKE[state]}
        strokeWidth={selected ? 2 : 1}
      />
      <text
        x={pos.cx}
        y={pos.cy - 4}
        textAnchor="middle"
        fill="#f8fafc"
        fontSize={11}
        fontWeight={600}
        style={{ pointerEvents: "none" }}
      >
        {label}
      </text>
      <text
        x={pos.cx}
        y={pos.cy + 10}
        textAnchor="middle"
        fill="#94a3b8"
        fontSize={9}
        style={{ pointerEvents: "none" }}
      >
        {state}
      </text>
      {state === "active" && (
        <circle
          cx={pos.x + 8}
          cy={pos.y + 8}
          r={3}
          fill="#60a5fa"
        >
          <animate
            attributeName="opacity"
            values="0.3;1;0.3"
            dur="1.2s"
            repeatCount="indefinite"
          />
        </circle>
      )}
    </g>
  );
}


interface EdgeLineProps {
  from: NodePos;
  to: NodePos;
  kind: string;
}


function EdgeLine({ from, to }: EdgeLineProps): JSX.Element {
  // Single CSS keyframe pulse — bounded lifecycle since React unmounts
  // the line once the edge falls off the 24-event window.
  const fromY = from.cy + NODE_HEIGHT / 2 - 4;
  const toY = to.cy - NODE_HEIGHT / 2 + 4;
  return (
    <g>
      <line
        x1={from.cx} y1={fromY}
        x2={to.cx} y2={toY}
        stroke="#60a5fa"
        strokeWidth={1.5}
        opacity={0.4}
      >
        <animate
          attributeName="opacity"
          values="1;0.4"
          dur="1.4s"
          repeatCount="1"
          fill="freeze"
        />
      </line>
      <circle r={4} fill="#fbbf24">
        <animateMotion
          dur="1.2s"
          repeatCount="1"
          fill="freeze"
          path={`M${from.cx},${fromY} L${to.cx},${toY}`}
        />
      </circle>
    </g>
  );
}
